import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

const EditMedicineModal = ({ mounted, show, medicine, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    service_item_name: '',
    partner_price: '',
    partner_discount: '',
    unit: '',
    active: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && medicine) {
      setFormData({
        service_item_name: medicine.service_item_name || '',
        partner_price: medicine.partner_price !== undefined ? medicine.partner_price.toString() : '',
        partner_discount: medicine.partner_discount !== undefined ? medicine.partner_discount.toString() : '',
        unit: medicine.unit !== undefined ? medicine.unit.toString() : '',
        active: medicine.active !== undefined ? medicine.active : true
      });
      setErrors({});
    }
  }, [show, medicine]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.service_item_name || formData.service_item_name.trim() === '') {
      newErrors.service_item_name = 'Medicine name is required';
    }
    
    const price = parseFloat(formData.partner_price);
    if (!formData.partner_price || formData.partner_price === '') {
      newErrors.partner_price = 'MRP is required';
    } else if (isNaN(price) || price < 0) {
      newErrors.partner_price = 'MRP must be a number >= 0';
    }
    
    const discount = parseFloat(formData.partner_discount);
    if (!formData.partner_discount || formData.partner_discount === '') {
      newErrors.partner_discount = 'AyushPay MRP is required';
    } else if (isNaN(discount) || discount < 0) {
      newErrors.partner_discount = 'AyushPay MRP must be a number >= 0';
    } else if (!isNaN(price) && discount > price) {
      newErrors.partner_discount = 'AyushPay MRP cannot exceed MRP';
    }
    
    const unit = parseInt(formData.unit);
    if (formData.unit === '' || formData.unit === undefined) {
      newErrors.unit = 'Unit is required';
    } else if (isNaN(unit) || unit < 0) {
      newErrors.unit = 'Unit must be a number >= 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        const submitData = {
          service_item_name: formData.service_item_name.trim(),
          partner_price: parseFloat(formData.partner_price),
          partner_discount: parseFloat(formData.partner_discount),
          unit: parseInt(formData.unit),
          active: formData.active
        };
        await onSubmit(medicine.id, submitData);
      } catch (error) {
        setErrors({ api: error.message || 'Failed to update medicine' });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!mounted || !show || !medicine) return null;

  return createPortal(
    <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl m-6 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">Edit Medicine</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {errors.api && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{errors.api}</div>}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medicine Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.service_item_name ? 'border-red-500' : 'border-gray-300'}`}
                value={formData.service_item_name}
                onChange={(e) => handleChange('service_item_name', e.target.value)}
                maxLength={255}
              />
              {errors.service_item_name && <p className="text-xs text-red-500 mt-1">{errors.service_item_name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                MRP (Partner Price) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.partner_price ? 'border-red-500' : 'border-gray-300'}`}
                value={formData.partner_price}
                onChange={(e) => handleChange('partner_price', e.target.value)}
              />
              {errors.partner_price && <p className="text-xs text-red-500 mt-1">{errors.partner_price}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AyushPay MRP (Partner Discount) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.partner_discount ? 'border-red-500' : 'border-gray-300'}`}
                value={formData.partner_discount}
                onChange={(e) => handleChange('partner_discount', e.target.value)}
              />
              {errors.partner_discount && <p className="text-xs text-red-500 mt-1">{errors.partner_discount}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.unit ? 'border-red-500' : 'border-gray-300'}`}
                value={formData.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
              />
              {errors.unit && <p className="text-xs text-red-500 mt-1">{errors.unit}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                value={formData.active ? 'true' : 'false'}
                onChange={(e) => handleChange('active', e.target.value === 'true')}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Medicine'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditMedicineModal;

